import { useState } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Alert, Divider, Space, Switch, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import truncate from "lodash.truncate";
import { CreditCard, Dumbbell, Ghost, Salad } from "lucide-react";
import { useTranslation } from "react-i18next";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { ProductDurationType, type RecurringInterval } from "@fitness-app/data-models/entities/Product";
import { type ProductClient, type SubscriptionProductClient } from "@fitness-app/data-models/entities/ProductClient";
import { TraineeStatus, type TraineeProfile } from "@fitness-app/data-models/entities/Trainee";

import ListItem from "~/components/ListItem/ListItem";
import { usePermission } from "~/hooks/usePermission";
import { useLoading } from "~/hooks/useLoading";
import { useClientActivityReport } from "~/modules/Trainee/hooks/useClientActivity";
import { usePendingClientPackage } from "~/modules/Trainee/hooks/usePendingClientPackage";
import { useTraineeActiveProgram, useTraineeNavigate } from "~/modules/Trainee/TraineeProfile/scope";
import { ClientPackageDrawer } from "./ClientPackageDrawer/ClientPackageDrawer";
import GhostStatusPopover from "./GhostStatusPopover";
import { useToggleProgramAccess, useUpdateTrainee } from "./useTraineeProfileMutations";

interface TraineeAccessProps {
  trainee: TraineeProfile;
  shouldDisableOption: boolean;
}

export const ScheduledAccessTag = ({
  scheduledAccessPeriod,
  accessPeriod,
}: {
  scheduledAccessPeriod: SubscriptionProductClient["scheduledAccessPeriod"];
  accessPeriod: ProductClient["accessPeriod"];
}) => {
  if (!scheduledAccessPeriod?.[0]) {
    return null;
  }

  let currentEnd = accessPeriod?.currentPeriodEnd;
  if (accessPeriod?.type === "recurring" && accessPeriod?.installments) {
    currentEnd =
      accessPeriod.pauseCollection?.resumes_at || accessPeriod.endDate || accessPeriod.installmentEndsAt || currentEnd;
  }

  const lastItemMetadata = scheduledAccessPeriod.at(-1)?.metadata;

  if (lastItemMetadata?.customDurationCount && lastItemMetadata?.customDurationUnit) {
    let startDate = dayjs.unix(
      scheduledAccessPeriod[0].isOverlappingSubscription && currentEnd
        ? currentEnd
        : scheduledAccessPeriod[0].start_date,
    );

    if (currentEnd && accessPeriod?.type === "recurring" && accessPeriod?.customDuration) {
      startDate = dayjs.unix(currentEnd);
    }

    const endDate = startDate.add(
      Number(lastItemMetadata.customDurationCount),
      lastItemMetadata.customDurationUnit as RecurringInterval,
    );

    return (
      <Tooltip
        title={
          <span>
            Dostęp przedłużony od {startDate.format("DD.MM.YYYY")} do {endDate.format("DD.MM.YYYY")}
          </span>
        }
      >
        <Tag color="green">Dostęp przedłużony (start {startDate.format("DD.MM.YYYY")})</Tag>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        <span>
          Dostęp przedłużony od{" "}
          {dayjs
            .unix(
              scheduledAccessPeriod[0].isOverlappingSubscription && currentEnd
                ? currentEnd
                : scheduledAccessPeriod[0].start_date,
            )
            .format("DD.MM.YYYY")}{" "}
          do {dayjs.unix(scheduledAccessPeriod.at(-1)?.end_date || 0).format("DD.MM.YYYY")}
        </span>
      }
    >
      <Tag color="green">
        Dostęp przedłużony (start{" "}
        {dayjs
          .unix(
            scheduledAccessPeriod[0].isOverlappingSubscription && currentEnd
              ? currentEnd
              : scheduledAccessPeriod[0].start_date,
          )
          .format("DD.MM.YYYY")}
        )
      </Tag>
    </Tooltip>
  );
};

const AccessPeriod = ({
  productClient,
  onOpenDetails,
}: {
  productClient: TraineeProfile["productClient"];
  onOpenDetails: () => void;
}) => {
  const { t } = useTranslation("trainees");

  if (!productClient) {
    return null;
  }

  if (
    productClient.accessPeriod &&
    productClient.accessPeriod?.type === "recurring" &&
    productClient.accessPeriod.installments
  ) {
    return (
      <div className="space-y-1">
        <Typography.Link onClick={onOpenDetails}>
          {productClient.accessPeriod.startAt
            ? `${dayjs.unix(productClient.accessPeriod.startAt).format("DD.MM.YYYY")} - `
            : "do "}
          {productClient.accessPeriod?.endDate || productClient.accessPeriod?.installmentEndsAt
            ? dayjs
                .unix(
                  productClient.accessPeriod.pauseCollection?.resumes_at ||
                    productClient.accessPeriod.endDate ||
                    productClient.accessPeriod.installmentEndsAt!,
                )
                .format("DD.MM.YYYY")
            : "- bezterminowo"}
        </Typography.Link>
        <ScheduledAccessTag
          scheduledAccessPeriod={productClient.scheduledAccessPeriod}
          accessPeriod={productClient.accessPeriod}
        />
      </div>
    );
  }

  if (productClient.accessPeriod) {
    return (
      <div className="space-y-1">
        <Typography.Link onClick={onOpenDetails}>
          {productClient.accessPeriod.currentPeriodStart
            ? `${dayjs.unix(productClient.accessPeriod.currentPeriodStart).format("DD.MM.YYYY")} - `
            : "do "}
          {productClient.accessPeriod?.currentPeriodEnd
            ? dayjs.unix(productClient.accessPeriod.currentPeriodEnd).format("DD.MM.YYYY")
            : "- bezterminowo"}
        </Typography.Link>
        <ScheduledAccessTag
          accessPeriod={productClient.accessPeriod}
          scheduledAccessPeriod={productClient.scheduledAccessPeriod}
        />
      </div>
    );
  }

  if (productClient.productDuration) {
    return (
      <Typography.Link onClick={onOpenDetails}>
        {productClient.productDuration.type === ProductDurationType.LastsFor
          ? `${t(`products:recurring.${productClient.productDuration.unit}`, {
              count: productClient.productDuration.count,
            })} - ${dayjs.unix(productClient.productDuration.startAt).format("DD.MM.YYYY")} - ${dayjs
              .unix(productClient.productDuration.endAt)
              .format("DD.MM.YYYY")}`
          : productClient.productDuration.type === ProductDurationType.UntilDate
            ? `${dayjs.unix(productClient.productDuration.startAt).format("DD.MM.YYYY")} - ${dayjs
                .unix(productClient.productDuration.endAt)
                .format("DD.MM.YYYY")}`
            : t(`products:productDurationType.${productClient.productDuration.type}`)}
      </Typography.Link>
    );
  }

  return (
    <Typography.Link onClick={onOpenDetails}>
      {t("tableHeader.connectedProduct")}: {productClient.product?.name ?? "-"}
    </Typography.Link>
  );
};

/**
 * A package bought while the current one still runs is a separate, not yet
 * assigned product_client — without this tag the profile card shows only the
 * old package and support reads "no renewal" off a client who already paid
 * (TRE-1899). The switch itself is done by the delayed stacking task at the
 * current access end.
 */
const PendingPackageTag = ({ email, currentProductId }: { email: string; currentProductId: string | undefined }) => {
  const { data } = usePendingClientPackage(email, currentProductId);

  if (!data) {
    return null;
  }

  const productName = data.pendingPackage.accessPeriod?.productName ?? "nowy pakiet";

  return (
    <Tooltip
      title={
        <span>
          Klientka ma opłacone kolejne przedłużenie ({productName}). Automat przepnie ją na nowy pakiet po zakończeniu
          obecnego dostępu{data.activationAt ? ` — ${dayjs.unix(data.activationAt).format("DD.MM.YYYY")}` : ""}.
        </span>
      }
    >
      <Tag color="blue">
        Kupione przedłużenie
        {data.activationAt ? ` (przepięcie ${dayjs.unix(data.activationAt).format("DD.MM.YYYY")})` : ""}
      </Tag>
    </Tooltip>
  );
};

const TraineeAccess = ({ trainee, shouldDisableOption }: TraineeAccessProps) => {
  const { t } = useTranslation("trainees");
  const selectedProgram = useTraineeActiveProgram().data?.plan;
  const navigate = useTraineeNavigate();
  const [loading, startLoading, stopLoading] = useLoading();
  const [loadingNutrition, startNutritionLoading, stopNutritionLoading] = useLoading();
  const [loadingGhost, startGhostLoading, stopGhostLoading] = useLoading();
  const { data: clientActivityReport } = useClientActivityReport(trainee.id, !!trainee.ghost);
  const updateTrainee = useUpdateTrainee();
  const toggleProgramAccessMutation = useToggleProgramAccess();
  const [packageDrawerOpen, setPackageDrawerOpen] = useState(false);

  const canManagePrograms = usePermission(PermissionKey.PROGRAMS_MANAGE);
  const canManageNutrition = usePermission(PermissionKey.NUTRITION_MANAGE);
  // The ghost flag drives the renewal KPI, so it stays above the trainer whose
  // client it describes: head coach, administration and the owner may flip it,
  // an ASSIGNED-scoped trainer may not. Mirrored by the guard trigger on
  // public.trainee — this only hides the switch.
  const canChangeGhostStatus = usePermission(PermissionKey.CLIENTS_WRITE, PermissionScope.TEAM);

  const toggleProgramAccess = async (checked: boolean) => {
    if (selectedProgram) {
      startLoading();
      // Błędy przełączników są ciche, jak wcześniej przy dispatchu bez `unwrap()`.
      await toggleProgramAccessMutation
        .mutateAsync({ programId: selectedProgram.id, hasAccessToProgram: checked })
        .catch(() => undefined);
      stopLoading();
    }
  };

  const toggleNutritionAccess = async (checked: boolean) => {
    if (trainee.nutrition) {
      startNutritionLoading();
      await updateTrainee.mutateAsync({ hasActiveNutrition: checked }).catch(() => undefined);
      stopNutritionLoading();
    }
  };

  const toggleGhostAccess = async (checked: boolean) => {
    if (trainee.id) {
      startGhostLoading();
      await updateTrainee.mutateAsync({ ghost: checked }).catch(() => undefined);
      stopGhostLoading();
    }
  };

  return (
    <>
      <ListItem
        title={t<string>("access.paid")}
        desc={
          <div className="space-y-1">
            <AccessPeriod productClient={trainee.productClient} onOpenDetails={() => setPackageDrawerOpen(true)} />
            <PendingPackageTag email={trainee.email} currentProductId={trainee.productClient?.productId} />
          </div>
        }
        iconPath={<CreditCard />}
        endContent={<Switch checked={trainee.productClient?.status === "active"} disabled />}
      />
      {/* Montowana tylko otwarta: zamknięta i tak nic nie renderuje, a jej `useNavigate` renderowałby
          nagłówek każdej otwartej karty przy każdej nawigacji. */}
      {packageDrawerOpen ? (
        <ClientPackageDrawer
          email={trainee.email}
          productId={trainee.productClient?.productId}
          onClose={() => setPackageDrawerOpen(false)}
        />
      ) : null}
      <Divider size="small" />
      <ListItem
        title="Plan treningowy"
        desc={
          !selectedProgram ? (
            t<string>("access.lackOfPlan")
          ) : (
            <Tooltip title={selectedProgram.name}>
              <Typography.Link
                strong
                onClick={() => navigate(`/trainee/details/${trainee.id}?tab=programs`)}
                style={{
                  maxWidth: "88%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {truncate(selectedProgram.name, { length: 28 })}
              </Typography.Link>
            </Tooltip>
          )
        }
        iconPath={<Dumbbell />}
        endContent={
          <Switch
            checked={Boolean(trainee.hasActiveTrainingProgram)}
            onClick={toggleProgramAccess}
            loading={loading}
            disabled={!trainee.activeTrainingProgramId || shouldDisableOption || !canManagePrograms}
          />
        }
      />
      {!trainee.hasActiveTrainingProgram && trainee.activeTrainingProgramId ? (
        <Alert message="Plan nie jest widoczny dla klienta" banner />
      ) : null}
      <Divider size="small" />
      <ListItem
        title={t<string>("access.mealsPlan")}
        desc={
          !trainee.nutrition ? (
            t<string>("access.lackOfPlan")
          ) : (
            <Tooltip title={trainee.nutrition.name}>
              <Typography.Link
                strong
                onClick={() =>
                  navigate(`/trainee/details/${trainee.id}?tab=nutrition&planId=${trainee?.nutrition?.id}`)
                }
                style={{
                  maxWidth: "88%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {truncate(trainee.nutrition.name, { length: 28 })}
              </Typography.Link>
            </Tooltip>
          )
        }
        iconPath={<Salad />}
        endContent={
          <Space direction="vertical">
            <Switch
              checked={Boolean(trainee.hasActiveNutrition)}
              disabled={!trainee.activeMealsPlanId || shouldDisableOption || !canManageNutrition}
              onClick={toggleNutritionAccess}
              loading={loadingNutrition}
            />
          </Space>
        }
      />
      {!trainee.hasActiveNutrition && trainee.nutrition ? (
        <Alert message="Dieta nie jest widoczna dla klienta" banner />
      ) : null}
      <Divider size="small" />
      <ListItem
        title={t<string>("access.isGhost")}
        desc={
          <Space direction="vertical">
            <Tooltip title={t<string>("access.ghostDescriptionLong")}>
              <span>
                {t<string>("access.ghostDescription")} <InfoCircleOutlined className="ml-1" />
              </span>
            </Tooltip>
            {clientActivityReport && (
              <GhostStatusPopover isGhost={trainee.ghost} clientActivityReport={clientActivityReport} />
            )}
          </Space>
        }
        iconPath={<Ghost />}
        endContent={
          <Space direction="vertical">
            <Switch
              checked={trainee.ghost ?? false}
              disabled={shouldDisableOption || !canChangeGhostStatus || trainee.status !== TraineeStatus.ACTIVE}
              onClick={toggleGhostAccess}
              loading={loadingGhost}
            />
          </Space>
        }
      />
    </>
  );
};

export default TraineeAccess;
